/**
 * usePlans - Hook for fetching subscription plans from the server
 * Used on customer-facing pages to dynamically display admin-managed plans
 */

import { useState, useEffect, useCallback } from 'react';

export interface PlanFeatures {
  maxWorkspaces: number;
  maxNodesPerWorkspace: number;
  maxStorageMB: number;
  aiGenerationsPerDay: number;
  cloudSyncEnabled: boolean;
  videoGenerationEnabled: boolean;
  exportWatermark: boolean;
}

export interface SubscriptionPlan {
  id: string;
  tier: string;
  displayName: string;
  description: string;
  price: number;
  currency: string;
  billingPeriod: string;
  features: PlanFeatures;
  sortOrder: number;
  badge: string | null;
}

const API_URL = '/.netlify/functions/get-plans';

export function usePlans() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (err: any) {
      console.error('[usePlans] Error:', err.message);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Helper: get plans grouped by tier
  const plansByTier = plans.reduce<Record<string, SubscriptionPlan[]>>((acc, plan) => {
    if (!acc[plan.tier]) acc[plan.tier] = [];
    acc[plan.tier].push(plan);
    return acc;
  }, {});

  // Helper: get the primary plan for each tier (first active one by sortOrder)
  const primaryPlanByTier = Object.entries(plansByTier).reduce<Record<string, SubscriptionPlan>>((acc, [tier, tierPlans]) => {
    acc[tier] = tierPlans[0]; // Already sorted by sortOrder from API
    return acc;
  }, {});

  return {
    plans,
    plansByTier,
    primaryPlanByTier,
    isLoading,
    error,
    refresh: fetchPlans,
  };
}
